// ABOUTME: The send-off timeline as a hook: stages 1 to 4 at 0, 700, 1600 and 3700 ms, and the exit at 4700 ms.
// ABOUTME: One start makes one send-off; reset cancels it and returns to rest; unmount clears every timer.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SendOffStage } from './screenMotion';

// ms after the press at which each stage begins (stage 1 is immediate), then the moment the exit navigates.
const STAGE_AT_MS: [SendOffStage, number][] = [[2, 700], [3, 1600], [4, 3700]];
const ELAPSED_AT_MS = 4700;

interface UseSendOffOptions {
  onElapsed: () => void;
}

export interface SendOff {
  stage: SendOffStage;
  active: boolean;
  elapsed: boolean;
  start: () => void;
  reset: () => void;
}

export function useSendOff({ onElapsed }: UseSendOffOptions): SendOff {
  const [stage, setStage] = useState<SendOffStage>(0);
  const [elapsed, setElapsed] = useState(false);
  const timers = useRef<number[]>([]);
  const activeRef = useRef(false);
  const onElapsedRef = useRef(onElapsed);
  useEffect(() => { onElapsedRef.current = onElapsed; });

  const clearTimers = useCallback(() => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  }, []);

  const start = useCallback(() => {
    if (activeRef.current) return;
    activeRef.current = true;
    setStage(1);
    timers.current = STAGE_AT_MS.map(([next, ms]) => window.setTimeout(() => setStage(next), ms));
    timers.current.push(window.setTimeout(() => {
      setElapsed(true);
      onElapsedRef.current();
    }, ELAPSED_AT_MS));
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    activeRef.current = false;
    setStage(0);
    setElapsed(false);
  }, [clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  return { stage, active: stage > 0, elapsed, start, reset };
}
